// ============================================================
// Swiss Finance OS - Transaction Review Modal
// Manual review workflow with Bezugsteuer detection
// Phase 1: Manual Review Workflow
// ============================================================

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { checkBezugsteuer } from '@/services/bookingService';
import { formatSwissCurrency } from '@/utils/format';

interface TransactionReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: any;
  onApprove: () => void;
  onReject: (reason?: string) => void;
}

export const TransactionReviewModal: React.FC<TransactionReviewModalProps> = ({
  open,
  onOpenChange,
  transaction,
  onApprove,
  onReject
}) => {
  const [checklist, setChecklist] = useState({
    amount: false,
    account: false,
    vat: false,
    counterparty: false,
    description: false,
    bezugsteuer: false
  });
  
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const bezugsteuerCheck = transaction ? checkBezugsteuer(transaction) : { required: false, reason: '' };
  const aiClass = transaction?.ai_classification || {};
  
  const allChecked = Object.values(checklist).every(v => v);
  
  const handleCheckItem = (key: keyof typeof checklist) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApprove = () => {
    onApprove();
    onOpenChange(false);
    // Reset
    setChecklist({
      amount: false,
      account: false,
      vat: false,
      counterparty: false,
      description: false,
      bezugsteuer: false
    });
    setNotes('');
  };

  const handleReject = () => {
    onReject(rejectReason || 'Manuell abgelehnt');
    onOpenChange(false);
    setRejectReason('');
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <i className="fa-solid fa-clipboard-check text-brand-accent"></i>
            Transaktion überprüfen
          </DialogTitle>
          <DialogDescription>
            Überprüfen Sie die AI-Klassifizierung vor der Buchung
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Transaction Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600">Geschäftspartner</p>
                <p className="font-semibold">{transaction.counterparty || 'Unbekannt'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Betrag</p>
                <p className="text-2xl font-bold">{formatSwissCurrency(transaction.amount)}</p>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">Beschreibung</p>
              <p className="text-sm">{transaction.description}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                <p className="text-sm text-gray-600">Datum</p>
                <p className="text-sm font-medium">{new Date(transaction.transaction_date).toLocaleDateString('de-CH')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Referenz</p>
                <p className="text-sm font-medium">{transaction.reference || '-'}</p>
              </div>
            </div>
          </div>

          {/* AI Classification */}
          <div className="border-l-4 border-blue-500 bg-blue-50 rounded-r-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-blue-900">AI Klassifizierung</h3>
              <Badge variant="outline" className="bg-white">
                {transaction.ai_classification_confidence || 0}% Vertrauen
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-blue-700">Konto</p>
                <p className="font-mono font-semibold">{aiClass.accountNumber || '-'}</p>
                <p className="text-xs text-gray-600">{aiClass.accountName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-blue-700">MwSt-Satz</p>
                <p className="font-semibold">{aiClass.vatRate || 0}%</p>
                <p className="text-xs text-gray-600">{aiClass.vatCode || '-'}</p>
              </div>
            </div>
            
            {aiClass.reasoning && (
              <div>
                <p className="text-sm text-blue-700">AI Begründung</p>
                <p className="text-sm text-gray-700 italic">{aiClass.reasoning}</p>
              </div>
            )}
          </div>

          {/* Bezugsteuer Warning */}
          {bezugsteuerCheck.required && (
            <div className="border-l-4 border-orange-500 bg-orange-50 rounded-r-lg p-4">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-globe text-orange-600 text-xl mt-1"></i>
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">Bezugsteuer erforderlich</h3>
                  <p className="text-sm text-orange-800">{bezugsteuerCheck.reason}</p>
                  <p className="text-xs text-orange-600 mt-2">
                    ⚠️ Diese Transaktion wird mit Reverse-Charge Mechanismus gebucht (Ziffer 382 + 400)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Review Checklist */}
          <div className="space-y-3">
            <h3 className="font-semibold">Review Checklist</h3>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-amount" 
                  checked={checklist.amount}
                  onCheckedChange={() => handleCheckItem('amount')}
                />
                <Label htmlFor="check-amount" className="cursor-pointer">
                  Betrag ist korrekt ({formatSwissCurrency(transaction.amount)})
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-account" 
                  checked={checklist.account}
                  onCheckedChange={() => handleCheckItem('account')}
                />
                <Label htmlFor="check-account" className="cursor-pointer">
                  Konto korrekt klassifiziert ({aiClass.accountNumber} - {aiClass.accountName})
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-vat" 
                  checked={checklist.vat}
                  onCheckedChange={() => handleCheckItem('vat')}
                />
                <Label htmlFor="check-vat" className="cursor-pointer">
                  MwSt-Satz korrekt ({aiClass.vatRate || 0}%)
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-counterparty" 
                  checked={checklist.counterparty}
                  onCheckedChange={() => handleCheckItem('counterparty')}
                />
                <Label htmlFor="check-counterparty" className="cursor-pointer">
                  Geschäftspartner verifiziert ({transaction.counterparty})
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-description" 
                  checked={checklist.description}
                  onCheckedChange={() => handleCheckItem('description')}
                />
                <Label htmlFor="check-description" className="cursor-pointer">
                  Beschreibung vollständig
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="check-bezugsteuer" 
                  checked={checklist.bezugsteuer}
                  onCheckedChange={() => handleCheckItem('bezugsteuer')}
                />
                <Label htmlFor="check-bezugsteuer" className="cursor-pointer">
                  {bezugsteuerCheck.required 
                    ? '✓ Bezugsteuer-Anforderung bestätigt' 
                    : 'Keine Bezugsteuer erforderlich'}
                </Label>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Anmerkungen (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Bemerkungen zur Klassifizierung..."
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t">
            <div>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Abbrechen
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={handleReject}
              >
                <i className="fa-solid fa-times mr-2"></i>
                Ablehnen
              </Button>
              
              <Button
                onClick={handleApprove}
                disabled={!allChecked}
                className="bg-green-600 hover:bg-green-700"
              >
                <i className="fa-solid fa-check mr-2"></i>
                Genehmigen für Buchung
              </Button>
            </div>
          </div>
          
          {!allChecked && (
            <p className="text-xs text-gray-500 text-center">
              Bitte alle Punkte überprüfen, bevor Sie genehmigen
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
